import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Link} from "react-router";
import Icon from "../Icon";
import svgSortAsc from "../../svgs/arrow-up.svg";
import svgSortDesc from "../../svgs/arrow-down.svg";
import dollars from "../../dollar-format";
import {dateISOtoUS, dateToUS} from "../../date-converter";
import {fetchData} from "../Service/graphQlService";
import {GET_PROPERTIES_BY_INVESTOR_ID} from "../Graphql/ContactsQueries/contactsQuery";
import "./InvestorPropertiesTab.scss";

var PAGE_SIZE_OPTIONS = [25, 50, 100, 500];
var DEFAULT_PAGE_SIZE = 25;

function normalizeValue(v) {
  if (v === null || typeof v === "undefined") return "";
  return String(v);
}

function includesIgnoreCase(haystack, needle) {
  if (!needle) return true;
  return normalizeValue(haystack).toLowerCase().includes(String(needle).toLowerCase());
}

function formatCloseOfEscrow(value) {
  if (value === null || typeof value === "undefined" || value === "") return "";
  var str = String(value).trim();
  if (!str) return "";

  var n = Number(str);
  if (Number.isFinite(n) && n > 1e11) {
    var fromMs = new Date(n);
    if (!Number.isNaN(fromMs.getTime())) {
      return fromMs.toLocaleDateString("en-US");
    }
  }

  var isoUs = dateISOtoUS(str);
  if (isoUs) return isoUs;

  var us = dateToUS(str);
  if (us) return us;

  var parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-US");
  }

  return str;
}

function closeOfEscrowToTime(value) {
  if (value === null || typeof value === "undefined" || value === "") return 0;
  var str = String(value).trim();
  var n = Number(str);
  if (Number.isFinite(n) && n > 1e11) return n;

  var isoUs = dateISOtoUS(str);
  if (isoUs) {
    var isoParts = isoUs.split("/");
    return new Date(isoParts[2], isoParts[0] - 1, isoParts[1]).getTime();
  }

  var parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatOwnershipLabel(segment) {
  if (!segment) return "";
  var normalized = String(segment).toLowerCase();
  if (normalized === "owned") return "Owned";
  if (normalized === "sold") return "Sold";
  if (normalized === "active") return "Active";
  return segment;
}

function isClosedPropertyStatus(status) {
  return String(status || "").toLowerCase() === "closed";
}

function isStaleBuyerOwnership(row, investorId) {
  if (!row || !investorId || row.is_past_ownership) return false;
  var id = String(investorId);
  if (!row.buyer_id || String(row.buyer_id) !== id) return false;
  if (isClosedPropertyStatus(row.status)) return false;
  if (row.seller_id && String(row.seller_id) !== id) return true;
  return false;
}

function getOwnershipPeriodLabel(row, investorId) {
  if (!row) return "";
  if (row.is_past_ownership || isStaleBuyerOwnership(row, investorId)) return "Past";
  return "Current";
}

function getOwnershipLabelForInvestor(row, investorId) {
  if (!row || !investorId) return formatOwnershipLabel(row && row.property_segment);
  if (row.is_past_ownership) {
    var pastRole = row.history_ownership_role || row.property_segment;
    return "Past " + formatOwnershipLabel(pastRole);
  }
  if (isStaleBuyerOwnership(row, investorId)) return "Past Owned";
  var id = String(investorId);
  var buyerMatch = row.buyer_id && String(row.buyer_id) === id;
  var sellerMatch = row.seller_id && String(row.seller_id) === id;
  if (buyerMatch) return "Owned";
  if (sellerMatch) {
    return isClosedPropertyStatus(row.status) ? "Sold" : "Active";
  }
  return formatOwnershipLabel(row.property_segment);
}

function getInvestorRecordUrl(investorId) {
  var basePath = arc.path.base.endsWith("/") ? arc.path.base.slice(0, -1) : arc.path.base;
  return basePath + "/investor/" + investorId;
}

export default function InvestorPropertiesTab(props) {
  var investorId = props.investorId;

  var payloadState = useState({
    active: [],
    closed: [],
    historical: [],
    activeTotalCount: 0,
    closedTotalCount: 0,
    historicalTotalCount: 0,
  });
  var payload = payloadState[0];
  var setPayload = payloadState[1];

  var isBusyState = useState(false);
  var isBusy = isBusyState[0];
  var setIsBusy = isBusyState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var pageState = useState(1);
  var page = pageState[0];
  var setPage = pageState[1];

  var pageSizeState = useState(DEFAULT_PAGE_SIZE);
  var pageSize = pageSizeState[0];
  var setPageSize = pageSizeState[1];

  var serverRowsState = useState([]);
  var serverRows = serverRowsState[0];
  var setServerRows = serverRowsState[1];

  var totalCountState = useState(0);
  var totalCount = totalCountState[0];
  var setTotalCount = totalCountState[1];

  var sortState = useState(null);
  var sort = sortState[0];
  var setSort = sortState[1];

  var filtersState = useState({});
  var filters = filtersState[0];
  var setFilters = filtersState[1];

  var investorRecordUrl = useMemo(function() {
    return investorId ? getInvestorRecordUrl(investorId) : "";
  }, [investorId]);

  var fetchPage = useCallback(function() {
    if (!investorId) return;
    setIsBusy(true);
    setError(null);
    fetchData(GET_PROPERTIES_BY_INVESTOR_ID, {
      investor_id: String(investorId),
      pageOptions: {page: 1, pageSize: 500},
    }).then(function(res) {
      var data = res && res.data ? res.data : {};
      var nextPayload = data.getpropertiesByInvestorId || {};
      setPayload({
        active: nextPayload.active || [],
        closed: nextPayload.closed || [],
        historical: nextPayload.historical || [],
        activeTotalCount: nextPayload.activeTotalCount || 0,
        closedTotalCount: nextPayload.closedTotalCount || 0,
        historicalTotalCount: nextPayload.historicalTotalCount || 0,
      });
      setIsBusy(false);
    }).catch(function(e) {
      setError((e && e.message) ? e.message : "Failed to load properties.");
      setPayload({
        active: [],
        closed: [],
        historical: [],
        activeTotalCount: 0,
        closedTotalCount: 0,
        historicalTotalCount: 0,
      });
      setIsBusy(false);
    });
  }, [investorId]);

  useEffect(function() {
    fetchPage();
  }, [fetchPage]);

  useEffect(function() {
    setPage(1);
    setFilters({});
    setSort(null);
  }, [investorId]);

  var allMergedRows = useMemo(function() {
    var active = (payload.active || []).map(function(row) {
      return Object.assign({}, row, {
        property_segment: "Active",
        investor_list_source: "active",
        is_past_ownership: false,
      });
    });
    var closed = (payload.closed || []).map(function(row) {
      return Object.assign({}, row, {
        property_segment: "Owned",
        investor_list_source: "closed",
        is_past_ownership: false,
      });
    });
    var historical = (payload.historical || []).map(function(row) {
      return Object.assign({}, row, {
        property_segment: row.history_ownership_role || "Owned",
        investor_list_source: "historical",
        is_past_ownership: true,
      });
    });
    return active.concat(closed).concat(historical);
  }, [payload]);

  useEffect(function() {
    setServerRows(allMergedRows);
    setTotalCount(
      (payload.activeTotalCount || 0) +
      (payload.closedTotalCount || 0) +
      (payload.historicalTotalCount || 0)
    );
  }, [payload, allMergedRows]);

  var columns = useMemo(function() {
    var basePath = arc.path.base.endsWith("/") ? arc.path.base.slice(0, -1) : arc.path.base;

    return [
      {
        key: "ownership_period",
        label: "Period",
        sortable: true,
        filterable: true,
        render: function(row) {
          return getOwnershipPeriodLabel(row, investorId);
        },
      },
      {
        key: "property_segment",
        label: "Ownership",
        sortable: true,
        filterable: true,
        render: function(row) {
          return getOwnershipLabelForInvestor(row, investorId);
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        filterable: true,
        render: function(row) { return row.status || ""; },
      },
      {
        key: "close_of_escrow",
        label: "Close of Escrow",
        sortable: true,
        filterable: true,
        render: function(row) { return formatCloseOfEscrow(row.close_of_escrow); },
      },
      {
        key: "category",
        label: "Category",
        sortable: true,
        filterable: true,
        render: function(row) { return capitalize(row.category); },
      },
      {
        key: "type",
        label: "Type",
        sortable: true,
        filterable: true,
        render: function(row) { return row.type || ""; },
      },
      {
        key: "name",
        label: "Property Name",
        sortable: true,
        filterable: true,
        render: function(row) {
          return (
            <Link
              className="green"
              to={{
                pathname: row.arcimedes_row_id ? basePath + "/property/" + row.arcimedes_row_id : undefined,
                state: {fromListUrl: investorRecordUrl},
              }}
            >
              {row.name || "Untitled"}
            </Link>
          );
        },
      },
      {
        key: "address",
        label: "Address",
        sortable: true,
        filterable: true,
        render: function(row) { return row.address || ""; },
      },
      {
        key: "city",
        label: "City",
        sortable: true,
        filterable: true,
        render: function(row) { return row.city || ""; },
      },
      {
        key: "state",
        label: "State",
        sortable: true,
        filterable: true,
        render: function(row) { return row.state || ""; },
      },
      {
        key: "list_price",
        label: "List Price",
        sortable: true,
        filterable: true,
        align: "right",
        render: function(row) { return dollars(row.list_price); },
      },
    ];
  }, [investorId, investorRecordUrl]);

  var filteredRows = useMemo(function() {
    var f = filters || {};
    return (serverRows || []).filter(function(row) {
      return columns.every(function(col) {
        if (!col.filterable) return true;
        var q = f[col.key];
        if (!q) return true;
        if (col.key === "close_of_escrow") {
          return includesIgnoreCase(formatCloseOfEscrow(row.close_of_escrow), q);
        }
        if (col.key === "ownership_period") {
          return includesIgnoreCase(getOwnershipPeriodLabel(row, investorId), q);
        }
        if (col.key === "property_segment") {
          return includesIgnoreCase(getOwnershipLabelForInvestor(row, investorId), q);
        }
        return includesIgnoreCase(row[col.key], q);
      });
    });
  }, [columns, filters, serverRows, investorId]);

  var sortedRows = useMemo(function() {
    if (!sort || !sort.key || !sort.direction) return filteredRows;
    var key = sort.key;
    var dir = sort.direction === "asc" ? 1 : -1;

    var rows = filteredRows.slice();
    rows.sort(function(a, b) {
      var av = a ? a[key] : undefined;
      var bv = b ? b[key] : undefined;

      if (key === "close_of_escrow") {
        return (closeOfEscrowToTime(a && a.close_of_escrow) - closeOfEscrowToTime(b && b.close_of_escrow)) * dir;
      }

      var an = typeof av === "number" ? av : Number(av);
      var bn = typeof bv === "number" ? bv : Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;

      return normalizeValue(av).localeCompare(normalizeValue(bv)) * dir;
    });
    return rows;
  }, [filteredRows, sort]);

  var displayRows = useMemo(function() {
    var start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  var pageCount = pageSize > 0 ? Math.max(1, Math.ceil((totalCount || 0) / pageSize)) : 1;
  var canPrev = page > 1;
  var canNext = page < pageCount;

  var goPrevPage = useCallback(function() {
    setPage(function(p) { return Math.max(1, p - 1); });
  }, []);

  var goNextPage = useCallback(function() {
    setPage(function(p) { return p + 1; });
  }, []);

  var goFirstPage = useCallback(function() {
    setPage(1);
  }, []);

  var goLastPage = useCallback(function() {
    setPage(pageCount);
  }, [pageCount]);

  var onPageSizeChange = useCallback(function(e) {
    var val = e && e.target ? Number(e.target.value) : DEFAULT_PAGE_SIZE;
    if (!val) return;
    setPageSize(val);
    setPage(1);
  }, []);

  var onHeaderClick = useCallback(function(event) {
    var ct = event && event.currentTarget ? event.currentTarget : null;
    var colKey = ct && ct.dataset ? ct.dataset.colkey : null;
    if (!colKey) return;
    var col = columns.find(function(c) { return c.key === colKey; });
    if (!col || !col.sortable) return;

    setSort(function(prev) {
      if (!prev || prev.key !== col.key) return {key: col.key, direction: "asc"};
      if (prev.direction === "asc") return {key: col.key, direction: "desc"};
      return null;
    });
  }, [columns]);

  var onFilterChange = useCallback(function(event) {
    var ct = event && event.currentTarget ? event.currentTarget : null;
    var colKey = ct && ct.dataset ? ct.dataset.colkey : null;
    if (!colKey) return;
    var value = ct ? ct.value : "";
    setFilters(function(prev) {
      var next = Object.assign({}, prev || {});
      next[colKey] = value;
      return next;
    });
  }, []);

  var stopPropagation = useCallback(function(e) {
    if (e && e.stopPropagation) e.stopPropagation();
  }, []);

  var currentCount = (payload.activeTotalCount || 0) + (payload.closedTotalCount || 0);
  var pastCount = payload.historicalTotalCount || 0;

  return (
    <div className="InvestorPropertiesTab" data-is-busy={isBusy ? true : null}>
      <div className="InvestorPropertiesTab_summary">
        <span>{currentCount.toLocaleString()} current</span>
        <span>{pastCount.toLocaleString()} prior ownership</span>
      </div>
      <div className="SortTable_rounded-corner" />
      <table className="SortTable SortTable--investor-properties">
        <thead>
          <tr>
            {columns.map(function(col) {
              var isActive = !!(sort && sort.key === col.key);
              var direction = isActive ? sort.direction : "";
              return (
                <th
                  key={col.key}
                  className={(isActive ? "SortTable_th--active " : "") + "padding-left padding-right"}
                  style={{cursor: col.sortable ? "pointer" : "default"}}
                  data-colkey={col.key}
                  onClick={col.sortable ? onHeaderClick : undefined}
                >
                  <a style={{justifyContent: col.align === "right" ? "flex-end" : "space-between"}}>
                    <div
                      className={"SortTable_th_name" + (direction === "asc" || direction === "desc" ? " active" : "")}
                    >
                      <span className={"SortTable_th_name_icon top" + (direction === "asc" ? " active" : "")}>
                        <Icon svg={svgSortAsc} />
                      </span>
                      {col.label}
                      <span className={"SortTable_th_name_icon bottom" + (direction === "desc" ? " active" : "")}>
                        <Icon svg={svgSortDesc} />
                      </span>
                    </div>
                  </a>
                </th>
              );
            })}
          </tr>
          <tr>
            {columns.map(function(col) {
              return (
                <th
                  key={col.key + "-filter"}
                  className="padding-left padding-right InvestorPropertiesTab_filter-cell"
                  onClick={stopPropagation}
                >
                  {col.filterable ? (
                    <input
                      type="text"
                      className="InvestorPropertiesTab_filter-input"
                      value={filters[col.key] || ""}
                      placeholder={col.label}
                      data-colkey={col.key}
                      onChange={onFilterChange}
                      onClick={stopPropagation}
                    />
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {!isBusy && displayRows.length === 0 ? (
            <tr>
              <td className="padding-left padding-right" colSpan={columns.length}>
                No records found.
              </td>
            </tr>
          ) : null}
          {displayRows.map(function(row, idx) {
            var isPast = row.is_past_ownership || isStaleBuyerOwnership(row, investorId);
            return (
              <tr
                key={row.arcimedes_row_id || ((row.name || "row") + "-" + idx)}
                className={isPast ? "InvestorPropertiesTab_row--past" : ""}
              >
                {columns.map(function(col) {
                  return (
                    <td
                      key={col.key + "-" + idx}
                      className="padding-left padding-right"
                      style={{textAlign: col.align || "left"}}
                    >
                      {col.render(row)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="InvestorPropertiesTab_pagination">
        <div className="InvestorPropertiesTab_pagination_controls">
          <button type="button" className="button-new negative" onClick={goFirstPage} disabled={!canPrev || isBusy}>{"<<"}</button>
          <button type="button" className="button-new negative" onClick={goPrevPage} disabled={!canPrev || isBusy}>Previous</button>
          <button type="button" className="button-new positive" onClick={goNextPage} disabled={!canNext || isBusy}>Next</button>
          <div className="InvestorPropertiesTab_pagination_meta tableListresult">
            <span>Page <strong>{page}</strong> of <strong>{pageCount}</strong></span>
            <select className="custom-select" value={pageSize} disabled={isBusy || !totalCount} onChange={onPageSizeChange}>
              {PAGE_SIZE_OPTIONS.map(function(size) {
                return (
                  <option key={size} value={size}>Show {size}&nbsp;&nbsp;&nbsp;</option>
                );
              })}
            </select>
          </div>
          <button type="button" className="btn button-new negative" onClick={goLastPage} disabled={!canNext || isBusy}>{">>"}</button>
          <div className="InvestorPropertiesTab_total-count">
            <span>Total Count : {Number(totalCount || 0).toLocaleString()}</span>
          </div>
        </div>
        {error ? <div className="Alert Alert--error InvestorPropertiesTab_error">{error}</div> : null}
      </div>
    </div>
  );
}
